import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Filter, X, Users, AlertCircle, Calendar, PlusCircle, CalendarClock, Zap, Info } from 'lucide-react';

// Distinct icons per event type — colorblind-friendly (shape > color)
const eventIcons = {
  created: { Icon: PlusCircle, color: 'text-green-700', bg: 'bg-green-100', label: 'Created', tooltip: 'A new conflict was created on this day' },
  due: { Icon: CalendarClock, color: 'text-red-600', bg: 'bg-red-100', label: 'Due', tooltip: 'A conflict resolution is due by this day' },
  activity: { Icon: Zap, color: 'text-purple-600', bg: 'bg-purple-100', label: 'Activity', tooltip: 'A conflict had a status change or progress on this day' }
};

const moodEmojis = { 1: '😞', 2: '😕', 3: '😐', 4: '😊', 5: '😄' };
const moodBgColors = {
  1: 'bg-red-100',
  2: 'bg-orange-100',
  3: 'bg-amber-50',
  4: 'bg-green-50',
  5: 'bg-emerald-50'
};
const urgencyColors = {
  1: 'bg-blue-600',
  2: 'bg-blue-700',
  3: 'bg-amber-700',
  4: 'bg-orange-700',
  5: 'bg-red-600'
};
const urgencyLabels = { 1: 'Low', 2: 'Low-Med', 3: 'Medium', 4: 'Med-High', 5: 'Critical' };

function MentorCalendarView({ conflicts, moodData, connectedUsers, onSelectConflict }) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [showFilters, setShowFilters] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  // Filters
  const [filterUser, setFilterUser] = useState('all');
  const [filterMoodMin, setFilterMoodMin] = useState(1);
  const [filterMoodMax, setFilterMoodMax] = useState(5);
  const [filterUrgencyMin, setFilterUrgencyMin] = useState(1);
  const [filterUrgencyMax, setFilterUrgencyMax] = useState(5);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
    setSelectedDay(null);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
    setSelectedDay(null);
  };
  const goToToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
    setSelectedDay(null);
  };

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const days = [];
    // Leading blanks
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  }, [viewYear, viewMonth]);

  // Build data maps for the current month
  const { dayMoodMap, dayConflictMap } = useMemo(() => {
    const moods = {}; // { 'YYYY-MM-DD': [{ userId, userName, rating, details }] }
    const conflictEvents = {}; // { 'YYYY-MM-DD': [{ conflict, eventType }] }

    // Process mood data
    const targetUsers = filterUser === 'all' ? connectedUsers : connectedUsers.filter(u => u.id === filterUser);

    targetUsers.forEach(u => {
      const userMood = moodData[u.id];
      if (!userMood) return;
      Object.entries(userMood).forEach(([dateStr, val]) => {
        // Check if this date is in the current month
        const [y, m] = dateStr.split('-').map(Number);
        if (y !== viewYear || m - 1 !== viewMonth) return;
        const rating = val.rating || val;
        if (rating < filterMoodMin || rating > filterMoodMax) return;
        if (!moods[dateStr]) moods[dateStr] = [];
        moods[dateStr].push({
          userId: u.id,
          userName: u.name || u.email,
          rating: typeof rating === 'number' ? rating : parseInt(rating),
          details: val.details || null
        });
      });
    });

    // Process conflicts — map by created date, status change dates, and needByDate
    const targetUserIds = new Set(targetUsers.map(u => u.id));
    conflicts.forEach(c => {
      const urgency = c.urgency || 3;
      if (urgency < filterUrgencyMin || urgency > filterUrgencyMax) return;

      // Check if any target user is involved
      const involved = c.mentees?.some(m => targetUserIds.has(m.id)) || targetUserIds.has(c.createdBy);
      if (filterUser !== 'all' && !involved) return;

      // Created date
      if (c.createdAt) {
        const d = c.createdAt.split('T')[0];
        const [y, m] = d.split('-').map(Number);
        if (y === viewYear && m - 1 === viewMonth) {
          if (!conflictEvents[d]) conflictEvents[d] = [];
          conflictEvents[d].push({ conflict: c, eventType: 'created' });
        }
      }

      // Need-by date
      if (c.needByDate) {
        const d = c.needByDate.split('T')[0];
        const [y, m] = d.split('-').map(Number);
        if (y === viewYear && m - 1 === viewMonth) {
          if (!conflictEvents[d]) conflictEvents[d] = [];
          conflictEvents[d].push({ conflict: c, eventType: 'due' });
        }
      }

      // Status changes
      (c.statusHistory || []).forEach(sh => {
        if (!sh.timestamp) return;
        const d = sh.timestamp.split('T')[0];
        const [y, m] = d.split('-').map(Number);
        if (y === viewYear && m - 1 === viewMonth) {
          if (!conflictEvents[d]) conflictEvents[d] = [];
          // Avoid duplicate entries for same conflict on same day
          const existing = conflictEvents[d].find(e => e.conflict.id === c.id && e.eventType === 'activity');
          if (!existing) {
            conflictEvents[d].push({ conflict: c, eventType: 'activity' });
          }
        }
      });
    });

    return { dayMoodMap: moods, dayConflictMap: conflictEvents };
  }, [conflicts, moodData, connectedUsers, viewYear, viewMonth, filterUser, filterMoodMin, filterMoodMax, filterUrgencyMin, filterUrgencyMax]);

  const formatDateStr = (day) => {
    const mm = String(viewMonth + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    return `${viewYear}-${mm}-${dd}`;
  };

  const isToday = (day) => {
    return day === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();
  };

  const hasActiveFilters = filterUser !== 'all' || filterMoodMin > 1 || filterMoodMax < 5 || filterUrgencyMin > 1 || filterUrgencyMax < 5;

  const clearFilters = () => {
    setFilterUser('all');
    setFilterMoodMin(1);
    setFilterMoodMax(5);
    setFilterUrgencyMin(1);
    setFilterUrgencyMax(5);
  };

  // Day detail panel
  const selectedDateStr = selectedDay ? formatDateStr(selectedDay) : null;
  const selectedMoods = selectedDateStr ? (dayMoodMap[selectedDateStr] || []) : [];
  const selectedConflicts = selectedDateStr ? (dayConflictMap[selectedDateStr] || []) : [];

  return (
    <div className="space-y-4">
      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="p-2 hover:bg-stone-100 rounded-lg transition-colors" aria-label="Previous month">
            <ChevronLeft className="w-5 h-5 text-stone-600" aria-hidden="true" />
          </button>
          <h3 className="text-xl font-semibold text-stone-800 min-w-[200px] text-center">
            {monthNames[viewMonth]} {viewYear}
          </h3>
          <button onClick={nextMonth} className="p-2 hover:bg-stone-100 rounded-lg transition-colors" aria-label="Next month">
            <ChevronRight className="w-5 h-5 text-stone-600" aria-hidden="true" />
          </button>
          <button onClick={goToToday} className="ml-2 text-xs text-purple-600 hover:text-purple-800 font-medium px-2 py-1 bg-purple-50 rounded">
            Today
          </button>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            hasActiveFilters ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
          }`}
        >
          <Filter className="w-4 h-4" />
          Filters {hasActiveFilters && <span className="bg-purple-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">ON</span>}
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl border-2 border-purple-200 p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-stone-800 flex items-center gap-2"><Filter className="w-4 h-4 text-purple-600" /> Filters</h4>
            {hasActiveFilters && <button onClick={clearFilters} className="text-xs text-purple-600 hover:text-purple-800 font-medium">Clear all</button>}
          </div>

          {/* Participant filter */}
          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">Participant</label>
            <select
              value={filterUser}
              onChange={e => setFilterUser(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
            >
              <option value="all">All connected users ({connectedUsers.length})</option>
              {connectedUsers.map(u => (
                <option key={u.id} value={u.id}>{u.name || u.email}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Mood range */}
            <div>
              <label className="text-xs font-medium text-stone-600 mb-2 block">Mood Range</label>
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      onClick={() => {
                        if (n === filterMoodMin && n === filterMoodMax) { setFilterMoodMin(1); setFilterMoodMax(5); }
                        else if (n <= filterMoodMax) setFilterMoodMin(n);
                      }}
                      className={`w-8 h-8 rounded text-sm font-medium transition-all ${
                        n >= filterMoodMin && n <= filterMoodMax
                          ? 'bg-purple-600 text-white'
                          : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                      }`}
                    >
                      {moodEmojis[n]}
                    </button>
                  ))}
                </div>
                <span className="text-xs text-stone-500">{filterMoodMin} to {filterMoodMax}</span>
              </div>
              <div className="flex gap-1 mt-1">
                {[1, 2, 3, 4, 5].map(n => (
                  <button
                    key={`max-${n}`}
                    onClick={() => { if (n >= filterMoodMin) setFilterMoodMax(n); }}
                    className={`w-8 h-1.5 rounded-full transition-all ${
                      n >= filterMoodMin && n <= filterMoodMax ? 'bg-purple-400' : 'bg-stone-200'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Urgency range */}
            <div>
              <label className="text-xs font-medium text-stone-600 mb-2 block">Conflict Urgency</label>
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      onClick={() => {
                        if (n === filterUrgencyMin && n === filterUrgencyMax) { setFilterUrgencyMin(1); setFilterUrgencyMax(5); }
                        else if (n <= filterUrgencyMax) setFilterUrgencyMin(n);
                      }}
                      className={`w-8 h-8 rounded text-xs font-medium transition-all ${
                        n >= filterUrgencyMin && n <= filterUrgencyMax
                          ? `${urgencyColors[n]} text-white`
                          : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <span className="text-xs text-stone-500">{urgencyLabels[filterUrgencyMin]} to {urgencyLabels[filterUrgencyMax]}</span>
              </div>
              <div className="flex gap-1 mt-1">
                {[1, 2, 3, 4, 5].map(n => (
                  <button
                    key={`umax-${n}`}
                    onClick={() => { if (n >= filterUrgencyMin) setFilterUrgencyMax(n); }}
                    className={`w-8 h-1.5 rounded-full transition-all ${
                      n >= filterUrgencyMin && n <= filterUrgencyMax ? 'bg-purple-400' : 'bg-stone-200'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-5 text-sm text-stone-600 px-1 py-1">
        <div className="group relative flex items-center gap-2 cursor-help">
          <span className="bg-green-100 p-1.5 rounded-md"><PlusCircle className="w-5 h-5 text-green-700" /></span>
          <span className="font-semibold">Created</span>
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-stone-800 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-10">
            A new conflict was created on this day
            <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-stone-800" />
          </span>
        </div>
        <div className="group relative flex items-center gap-2 cursor-help">
          <span className="bg-red-100 p-1.5 rounded-md"><CalendarClock className="w-5 h-5 text-red-600" /></span>
          <span className="font-semibold">Due</span>
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-stone-800 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-10">
            A conflict's need-by deadline falls on this day
            <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-stone-800" />
          </span>
        </div>
        <div className="group relative flex items-center gap-2 cursor-help">
          <span className="bg-purple-100 p-1.5 rounded-md"><Zap className="w-5 h-5 text-purple-600" /></span>
          <span className="font-semibold">Activity</span>
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-stone-800 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-10">
            A conflict had a status change or phase transition on this day
            <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-stone-800" />
          </span>
        </div>
        <span className="text-stone-300 text-lg">|</span>
        <div className="group relative flex items-center gap-2 cursor-help">
          <span className="font-semibold">Mood</span>
          <span className="flex items-center gap-1">
            {[1,2,3,4,5].map(n => <span key={n} className="text-lg">{moodEmojis[n]}</span>)}
          </span>
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-stone-800 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-10">
            Daily mood check-ins from participants (1 = low, 5 = great)
            <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-stone-800" />
          </span>
        </div>
      </div>

      {/* Month Grid */}
      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-stone-200">
          {dayNames.map(d => (
            <div key={d} className="text-center text-sm font-medium text-stone-500 py-2.5 bg-stone-50">
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7">
          {calendarDays.map((day, idx) => {
            if (day === null) return <div key={`blank-${idx}`} className="min-h-[80px] bg-stone-50/50 border-b border-r border-stone-100" />;

            const dateStr = formatDateStr(day);
            const moods = dayMoodMap[dateStr] || [];
            const conflictEvts = dayConflictMap[dateStr] || [];
            const hasMood = moods.length > 0;
            const hasConflict = conflictEvts.length > 0;
            const isSelected = selectedDay === day;
            const todayFlag = isToday(day);
            // Average mood for background tinting
            const avgMood = hasMood ? Math.round(moods.reduce((s, m) => s + m.rating, 0) / moods.length) : null;

            return (
              <div
                key={day}
                onClick={() => setSelectedDay(isSelected ? null : day)}
                className={`min-h-[80px] border-b border-r border-stone-100 p-1 cursor-pointer transition-all hover:bg-purple-50 ${
                  isSelected ? 'ring-2 ring-purple-500 ring-inset bg-purple-50' : ''
                } ${todayFlag ? 'bg-amber-50/50' : ''}`}
              >
                {/* Day number */}
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-medium ${todayFlag ? 'bg-amber-700 text-white w-5 h-5 rounded-full flex items-center justify-center' : 'text-stone-600'}`}>
                    {day}
                  </span>
                </div>

                {/* Mood emoji row (top) */}
                {hasMood && (
                  <div className={`rounded px-0.5 py-0.5 mb-1 ${moodBgColors[avgMood] || 'bg-stone-50'}`}>
                    <div className="flex flex-wrap gap-0.5">
                      {moods.map((m, i) => (
                        <span key={i} className="text-base leading-tight" title={`${m.userName}: ${m.rating}/5${m.details ? `: "${m.details}"` : ''}`}>
                          {moodEmojis[m.rating]}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Conflict icons (bottom) */}
                {hasConflict && (
                  <div className="flex flex-wrap gap-0.5">
                    {conflictEvts.slice(0, 4).map((evt, i) => {
                      const urgency = evt.conflict.urgency || 3;
                      const iconSize = urgency >= 4 ? 'w-4 h-4' : 'w-3.5 h-3.5';
                      const IconComp = evt.eventType === 'created' ? PlusCircle
                        : evt.eventType === 'due' ? CalendarClock
                        : Zap;
                      const iconColor = evt.eventType === 'created' ? 'text-green-700'
                        : evt.eventType === 'due' ? 'text-red-600'
                        : 'text-purple-600';
                      const iconBg = evt.eventType === 'created' ? 'bg-green-50'
                        : evt.eventType === 'due' ? 'bg-red-50'
                        : 'bg-purple-50';
                      return (
                        <span key={i} className="group/dot relative">
                          <span className={`${iconBg} rounded p-0.5 inline-flex ${urgency >= 4 ? 'ring-1 ring-red-300' : ''}`}>
                            <IconComp className={`${iconSize} ${iconColor}`} />
                          </span>
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-stone-800 text-white text-[10px] rounded-md whitespace-nowrap opacity-0 group-hover/dot:opacity-100 transition-opacity pointer-events-none shadow-lg z-20 max-w-[160px] truncate">
                            {evt.conflict.title || 'Untitled'} · {evt.eventType} · urgency {urgency}
                          </span>
                        </span>
                      );
                    })}
                    {conflictEvts.length > 4 && (
                      <span className="text-[10px] text-stone-500 font-medium self-center">+{conflictEvts.length - 4}</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected Day Detail Panel */}
      {selectedDay && (
        <div className="bg-white rounded-xl border-2 border-purple-200 p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-stone-800">
              {monthNames[viewMonth]} {selectedDay}, {viewYear}
            </h4>
            <button onClick={() => setSelectedDay(null)} className="p-1 hover:bg-stone-100 rounded" aria-label="Close day view">
              <X className="w-4 h-4 text-stone-500" />
            </button>
          </div>

          {selectedMoods.length === 0 && selectedConflicts.length === 0 && (
            <p className="text-sm text-stone-500 text-center py-6">No mood or conflict data for this day.</p>
          )}

          {/* Mood entries */}
          {selectedMoods.length > 0 && (
            <div className="mb-4">
              <h5 className="text-xs font-semibold text-stone-600 uppercase tracking-wider mb-2">Mood Check-Ins</h5>
              <div className="space-y-2">
                {selectedMoods.map((m, i) => (
                  <div key={i} className={`p-3 rounded-lg ${moodBgColors[m.rating]} border border-stone-200`}>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{moodEmojis[m.rating]}</span>
                      <span className="text-sm font-medium text-stone-700">{m.userName}</span>
                      <span className="text-xs text-stone-500 ml-auto">{m.rating}/5</span>
                    </div>
                    {m.details && (
                      <p className="text-xs text-stone-600 mt-1.5 italic">"{m.details}"</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Conflict entries */}
          {selectedConflicts.length > 0 && (
            <div>
              <h5 className="text-xs font-semibold text-stone-600 uppercase tracking-wider mb-2">Conflict Activity</h5>
              <div className="space-y-2">
                {selectedConflicts.map((evt, i) => {
                  const c = evt.conflict;
                  const urgency = c.urgency || 3;
                  return (
                    <div
                      key={i}
                      onClick={() => onSelectConflict && onSelectConflict(c)}
                      className="p-3 bg-stone-50 rounded-lg border border-stone-200 hover:border-purple-300 hover:bg-purple-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-2.5">
                        {evt.eventType === 'created' ? <span className="bg-green-100 p-1 rounded"><PlusCircle className="w-5 h-5 text-green-700" /></span>
                          : evt.eventType === 'due' ? <span className="bg-red-100 p-1 rounded"><CalendarClock className="w-5 h-5 text-red-600" /></span>
                          : <span className="bg-purple-100 p-1 rounded"><Zap className="w-5 h-5 text-purple-600" /></span>}
                        <span className="text-sm font-medium text-stone-700 flex-1 truncate">{c.title || 'Untitled'}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                          urgency >= 4 ? 'bg-red-100 text-red-700' : urgency >= 3 ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          Urgency {urgency}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 text-xs text-stone-500">
                        <span className={`capitalize ${
                          evt.eventType === 'created' ? 'text-green-700' : evt.eventType === 'due' ? 'text-red-600' : 'text-purple-600'
                        } font-medium`}>
                          {evt.eventType === 'due' ? 'Due date' : evt.eventType === 'created' ? 'Created' : 'Status change'}
                        </span>
                        <span>·</span>
                        <span>{(c.status || '').replace(/-/g, ' ')}</span>
                        <span>·</span>
                        <span>{(c.mentees || []).map(m => m.name || m.email).join(', ')}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pattern Insights */}
      {hasActiveFilters && filterUser !== 'all' && (
        <div className="bg-purple-50 rounded-xl border border-purple-200 p-4">
          <h5 className="text-xs font-semibold text-purple-800 uppercase tracking-wider mb-2">Monthly Summary</h5>
          {(() => {
            const allMoods = Object.values(dayMoodMap).flat();
            const allConflicts = Object.values(dayConflictMap).flat();
            const avgMood = allMoods.length > 0 ? (allMoods.reduce((s, m) => s + m.rating, 0) / allMoods.length).toFixed(1) : null;
            const lowMoodDays = Object.entries(dayMoodMap).filter(([, moods]) => moods.some(m => m.rating <= 2)).length;
            const uniqueConflicts = new Set(allConflicts.map(e => e.conflict.id)).size;
            return (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 bg-white rounded-lg border border-purple-100">
                  <div className="text-lg font-semibold text-purple-800">{allMoods.length}</div>
                  <div className="text-[10px] text-stone-500">Mood entries</div>
                </div>
                <div className="p-3 bg-white rounded-lg border border-purple-100">
                  <div className="text-lg font-semibold text-purple-800">{avgMood || '—'}</div>
                  <div className="text-[10px] text-stone-500">Avg mood</div>
                </div>
                <div className="p-3 bg-white rounded-lg border border-purple-100">
                  <div className={`text-lg font-semibold ${lowMoodDays > 3 ? 'text-red-600' : 'text-purple-800'}`}>{lowMoodDays}</div>
                  <div className="text-[10px] text-stone-500">Low mood days</div>
                </div>
                <div className="p-3 bg-white rounded-lg border border-purple-100">
                  <div className="text-lg font-semibold text-purple-800">{uniqueConflicts}</div>
                  <div className="text-[10px] text-stone-500">Active conflicts</div>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

export default MentorCalendarView;
